import { Badge, Button, ConfirmDialog, Modal, Surface, Table, Td, Th, Tr, cn } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { usePermission } from '../../../auth/auth-store';
import { formatDate, formatDateTime, formatQty } from '../../../lib/format';
import { errorMessage, toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { receiptsApi, receivingKeys } from '../api';
import { RECEIPT_STATUS_TONE, linePct } from '../format';
import { CagePanel } from '../panels/CagePanel';
import { PalletizePanel } from '../panels/PalletizePanel';
import { ProducePanel } from '../panels/ProducePanel';
import { ReceivePanel } from '../panels/ReceivePanel';

const TABS = ['receive', 'palletize', 'produce', 'cage'] as const;
type Tab = (typeof TABS)[number];

/**
 * `/warehouse/receiving/:id` — the receiving session.
 *
 * One screen per delivery: the scan-driven receive flow, the FCL palletize
 * flow, the produce fast path and the CO2 cage session, over a live list of
 * what has been received so far and the PO's open lines.
 */
export function ReceiptSessionPage(): ReactNode {
  const { t } = useTranslation('receiving');
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const canClose = usePermission('orders.inbound.manage', 'warehouse.goods.receive');
  const [tab, setTab] = useState<Tab>('receive');
  const [closing, setClosing] = useState(false);
  const [closePo, setClosePo] = useState(false);
  const [voidLineId, setVoidLineId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: receivingKeys.receipt(id),
    queryFn: () => receiptsApi.detail(id),
    enabled: id.length > 0,
  });
  // Two operators can work one delivery; the lines list must not go stale.
  useLiveQuery(['receipts', 'stock'], receivingKeys.receipt(id));

  const close = useMutation({
    mutationFn: () => receiptsApi.close(id, { closePurchaseOrder: closePo }),
    onSuccess: async () => {
      toastSuccess(t('session.closed'));
      setClosing(false);
      await queryClient.invalidateQueries({ queryKey: ['receipts'] });
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
    onError: (error) => {
      setClosing(false);
      toastError(error);
    },
  });

  const voidLine = useMutation({
    mutationFn: (lineId: string) => receiptsApi.voidLine(id, lineId),
    onSuccess: async () => {
      toastSuccess(t('session.voided'));
      setVoidLineId(null);
      await queryClient.invalidateQueries({ queryKey: receivingKeys.receipt(id) });
    },
    onError: (error) => {
      setVoidLineId(null);
      toastError(error);
    },
  });

  if (query.isLoading) return <FullPageSpinner />;
  if (query.error || !query.data) {
    return (
      <>
        <PageHeader title={t('session.title')} />
        <Surface className="p-6 text-sm text-danger-600">{errorMessage(query.error)}</Surface>
      </>
    );
  }

  const receipt = query.data;
  const isOpen = receipt.status === 'open';

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/warehouse/receiving" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3.5" aria-hidden />
            {t('session.back')}
          </Link>
        }
        title={receipt.poNo ?? t('session.blindTitle')}
        subtitle={receipt.supplierName}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={RECEIPT_STATUS_TONE[receipt.status]} dot>
              {t(`receipts.statuses.${receipt.status}`)}
            </Badge>
            {isOpen && canClose ? (
              <Button
                icon={<CheckCircle2 className="size-4" />}
                onClick={() => {
                  setClosing(true);
                }}
              >
                {t('session.close')}
              </Button>
            ) : null}
          </div>
        }
      />

      {!isOpen ? (
        <Surface className="mb-4 p-3 text-sm text-ink-muted">{t('session.closedBanner')}</Surface>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="space-y-4">
          <nav className="flex flex-wrap gap-1" aria-label={t('session.title')}>
            {TABS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setTab(key);
                }}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-sm transition-colors',
                  tab === key
                    ? 'bg-brand-600 text-white'
                    : 'bg-surface-subtle text-ink-muted hover:text-ink',
                )}
                aria-current={tab === key ? 'page' : undefined}
              >
                {t(`session.tabs.${key}`)}
              </button>
            ))}
          </nav>

          {tab === 'receive' ? <ReceivePanel receipt={receipt} /> : null}
          {tab === 'palletize' ? <PalletizePanel receipt={receipt} /> : null}
          {tab === 'produce' ? <ProducePanel receipt={receipt} /> : null}
          {tab === 'cage' ? <CagePanel receipt={receipt} /> : null}

          <Surface className="overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3">
              <h3 className="text-sm font-medium text-ink">{t('session.lines')}</h3>
              <span className="text-xs text-ink-muted">{receipt.lines.length}</span>
            </div>
            <div className="overflow-x-auto scrollbar-thin">
              <Table>
                <thead>
                  <tr className="border-b border-surface-border">
                    <Th>{t('line.article')}</Th>
                    <Th>{t('line.lot')}</Th>
                    <Th>{t('line.bestBefore')}</Th>
                    <Th>{t('line.pallet')}</Th>
                    <Th className="text-right">{t('line.qty')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {receipt.lines.map((line) => (
                    <Tr key={line.id}>
                      <Td>
                        <div className="flex flex-col">
                          <span className="text-ink">{line.articleName}</span>
                          <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>
                        </div>
                      </Td>
                      <Td className="font-mono text-sm">{line.lotNo}</Td>
                      <Td>{formatDate(line.bestBefore)}</Td>
                      <Td className="font-mono text-sm">
                        {line.lpn ?? '—'}
                        {line.ssccAlias ? (
                          <span className="ml-1 text-xs text-ink-muted">({line.ssccAlias})</span>
                        ) : null}
                      </Td>
                      <Td className="text-right tabular-nums">{formatQty(line.qtyBase)}</Td>
                      <Td className="text-right">
                        {isOpen ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`${t('session.void')} ${line.articleNo}`}
                            onClick={() => {
                              setVoidLineId(line.id);
                            }}
                          >
                            <Undo2 className="size-4" aria-hidden />
                          </Button>
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                  {receipt.lines.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-sm text-ink-muted">
                        {t('session.noLines')}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          </Surface>
        </div>

        <aside className="space-y-3">
          <Surface className="p-4">
            <h3 className="text-sm font-medium text-ink">{t('session.poLines')}</h3>
            <p className="mt-1 text-xs text-ink-muted">{t('session.poLinesHint')}</p>
            <ul className="mt-3 space-y-3">
              {receipt.poLines.map((line) => (
                <li key={line.id}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm text-ink">{line.articleName}</span>
                    <span className="shrink-0 font-mono text-xs text-ink-muted">
                      {t('session.receivedOf', {
                        received: formatQty(line.receivedQtyBase),
                        ordered: formatQty(line.qtyBase),
                      })}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-subtle" aria-hidden>
                    <div className="h-full rounded-full bg-brand-600" style={{ width: `${linePct(line)}%` }} />
                  </div>
                </li>
              ))}
              {receipt.poLines.length === 0 ? (
                <li className="text-sm text-ink-muted">{t('receipts.blind')}</li>
              ) : null}
            </ul>
          </Surface>

          <Surface className="p-4 text-sm">
            <dl className="space-y-2">
              <Row label={t('receipts.startedBy')} value={receipt.startedByName ?? '—'} />
              <Row label={t('receipts.createdAt')} value={formatDateTime(receipt.createdAt)} />
              {receipt.closedAt ? (
                <Row label={t('receipts.closedAt')} value={formatDateTime(receipt.closedAt)} />
              ) : null}
              {receipt.note ? <Row label={t('receipts.note')} value={receipt.note} /> : null}
            </dl>
          </Surface>
        </aside>
      </div>

      {closing ? (
        <Modal
          open
          onClose={() => {
            setClosing(false);
          }}
          title={t('session.closeTitle')}
          width={520}
        >
          <div className="space-y-4">
            <p className="text-sm text-ink-muted">{t('session.closeBody')}</p>
            {receipt.poId ? (
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="size-4 rounded border-surface-border"
                  checked={closePo}
                  onChange={(event) => {
                    setClosePo(event.target.checked);
                  }}
                />
                {t('session.closePo')}
              </label>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setClosing(false);
                }}
              >
                {t('session.back')}
              </Button>
              <Button
                loading={close.isPending}
                onClick={() => {
                  close.mutate();
                }}
              >
                {t('session.close')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {voidLineId ? (
        <ConfirmDialog
          title={t('session.voidTitle')}
          message={t('session.voidBody')}
          confirmLabel={t('session.void')}
          variant="danger"
          onClose={() => {
            setVoidLineId(null);
          }}
          onConfirm={() => voidLine.mutateAsync(voidLineId).then(() => undefined)}
        />
      ) : null}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="text-right text-ink">{value}</dd>
    </div>
  );
}
